import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Brush, Undo2 } from 'lucide-react';
import { listUsers, setJanitor, undoJanitor, type UserRow } from '../api/janitors';

interface UserManagerProps {
  onClose: () => void;
}

const PLAN_LABEL = { pro: 'PRO', trial: 'TRIAL', code: 'GIFT', expired: 'EXPIRED' } as const;

// One tab per chip, so the two never disagree: Pro is actually paying, Trial
// hasn't been charged yet. Churned once had access and lost it (lapsed sub or
// gift); Inactive signed up and never subscribed at all.
type Tab = 'pro' | 'trial' | 'gift' | 'churned' | 'inactive';
const TABS: { id: Tab; label: string }[] = [
  { id: 'pro', label: 'Pro' }, { id: 'trial', label: 'Trial' }, { id: 'gift', label: 'Gift' },
  { id: 'churned', label: 'Churned' }, { id: 'inactive', label: 'Inactive' },
];
function inTab(u: UserRow, tab: Tab): boolean {
  switch (tab) {
    case 'pro': return u.plan === 'pro';
    case 'trial': return u.plan === 'trial';
    case 'gift': return u.plan === 'code';
    case 'churned': return u.plan === 'expired';
    default: return u.plan === null;
  }
}

const DAY = 24 * 60 * 60 * 1000;
// "42d", then months once days stop being readable, then years.
function timeLeft(until: number): string {
  const days = Math.max(0, Math.ceil((until - Date.now()) / DAY));
  if (days < 1) return '<1d';
  if (days <= 90) return `${days}d`;
  if (days < 350) return `${Math.round(days / 30)}mo`;
  return `${(days / 365).toFixed(1).replace(/\.0$/, '')}y`;
}
// A gift's length, in the unit it was sold in: "1mo", "3mo", "1y".
function giftLength(days: number): string {
  if (days >= 360) return `${Math.round(days / 365)}y`;
  if (days >= 28) return `${Math.round(days / 30)}mo`;
  return `${days}d`;
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function joinedShort(ms: number): string {
  const d = new Date(ms);
  return `${MONTHS[d.getMonth()]} ’${String(d.getFullYear()).slice(-2)}`;
}
const fullDate = (ms: number) => new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

// The plan chip carries the clock: "PRO · 42d", or "PRO · ENDS 42d" when
// they've cancelled and access lapses instead of renewing.
function planChip(u: UserRow) {
  if (!u.plan) return null;
  // typeof, not a null check: a listUsers deployed before this field exists
  // omits it, and NaN maths on undefined renders "NaNy".
  const left = typeof u.until === 'number' && u.plan !== 'expired' ? timeLeft(u.until) : null;
  // Gift: "GIFT 3mo · 42d" — how long it was for, then how long is left.
  const length = u.plan === 'code' && typeof u.giftDays === 'number' && u.giftDays > 0 ? ` ${giftLength(u.giftDays)}` : '';
  const label = left ? `${PLAN_LABEL[u.plan]}${length} · ${u.cancelled ? 'ENDS ' : ''}${left}` : `${PLAN_LABEL[u.plan]}${length}`;
  const title = left
    ? `${u.cancelled ? 'Cancelled — access ends' : u.plan === 'pro' ? 'Renews' : 'Ends'} ${fullDate(u.until as number)}`
    : undefined;
  return (
    <span className={`plan-chip plan-chip--${u.plan}${u.cancelled ? ' plan-chip--cancelled' : ''} user-row-chip`} title={title}>
      {label}
    </span>
  );
}

function errMsg(e: unknown, fallback: string): string {
  return (e as { message?: string })?.message?.replace(/^.*?:\s*/, '') || fallback;
}

// Admin only: every account in one filterable list. The brush toggles janitor
// status; janitors also get the pull-the-cord button that revokes them and
// reverts everything they did.
export default function UserManager({ onClose }: UserManagerProps) {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState<Tab>('pro');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = () => listUsers().then(
    (list) => { setUsers(list); setLoadErr(null); },
    (e) => setLoadErr(errMsg(e, 'Could not load users')),
  );
  useEffect(() => { void load(); }, []);

  // Janitors first (they're what this list is mostly for), then newest signups.
  const shown = useMemo(() => {
    if (!users) return [];
    const q = filter.trim().toLowerCase();
    return users
      .filter((u) => inTab(u, tab))
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .sort((a, b) => Number(b.janitor) - Number(a.janitor) || (b.created ?? 0) - (a.created ?? 0));
  }, [users, filter, tab]);
  const tabCount = (t: Tab) => users?.filter((u) => inTab(u, t)).length ?? 0;
  const janitorCount = users?.filter((u) => u.janitor).length ?? 0;

  const toggle = async (u: UserRow) => {
    if (busy) return;
    if (u.janitor && !confirm(`Remove ${u.name} as a janitor? Their past changes stay in place.`)) return;
    setBusy(u.uid);
    setErr(null);
    setNote(null);
    try {
      await setJanitor({ uid: u.uid }, !u.janitor);
      setNote(`${u.name} is ${u.janitor ? 'no longer' : 'now'} a janitor`);
      await load();
    } catch (e) {
      setErr(errMsg(e, 'Could not update'));
    } finally {
      setBusy(null);
    }
  };

  const undoAll = async (u: UserRow) => {
    if (busy) return;
    if (!confirm(`Revoke ${u.name} AND revert every change they made? This can take a minute.`)) return;
    setBusy(u.uid);
    setErr(null);
    setNote(null);
    try {
      const r = await undoJanitor(u.uid);
      const skipped = r.skipped.length ? ` · ${r.skipped.length} skipped (newer edits on top — see the log)` : '';
      setNote(`${u.name} revoked · ${r.reverted} change${r.reverted === 1 ? '' : 's'} reverted${skipped}`);
      await load();
    } catch (e) {
      setErr(errMsg(e, 'Undo failed'));
    } finally {
      setBusy(null);
    }
  };

  // Over the phone frame, not the whole viewport, so it works on a phone.
  return createPortal(
    <div className="frame-modal-backdrop" onClick={onClose}>
      <div className="gift-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gift-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <div className="code-table-head" style={{ marginBottom: 10 }}>
          <span>Users</span>
          {users && <span className="code-table-count">{users.length}</span>}
          {janitorCount > 0 && <span className="user-janitor-count"><Brush size={11} /> {janitorCount}</span>}
        </div>
        <input
          type="search"
          className="gift-modal-input user-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name or email"
          autoComplete="off"
        />
        <div className="admin-audit-tabs user-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`admin-audit-tab${tab === t.id ? ' admin-audit-tab--active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}{users && <span className="user-tab-count">{tabCount(t.id)}</span>}
            </button>
          ))}
        </div>
        {err && <span className="gift-modal-error user-note">{err}</span>}
        {note && <span className="gift-modal-success user-note">{note}</span>}

        {loadErr ? (
          <span className="gift-modal-error">{loadErr}</span>
        ) : !users ? (
          <span className="code-table-empty">Loading…</span>
        ) : shown.length === 0 ? (
          <span className="code-table-empty">No one matches.</span>
        ) : (
          <div className="user-list">
            {shown.map((u) => (
              <div key={u.uid} className={`user-row${u.janitor ? ' user-row--janitor' : ''}`}>
                <div className="user-row-who">
                  <div className="janitor-name">
                    <span className="user-row-name">{u.name}</span>
                    {planChip(u)}
                    {typeof u.created === 'number' && (
                      <span className="plan-chip user-row-chip user-row-chip--joined" title={`Joined ${fullDate(u.created)}`}>
                        Joined {joinedShort(u.created)}
                      </span>
                    )}
                  </div>
                  {u.email && u.email !== u.name && <div className="janitor-email">{u.email}</div>}
                  {u.janitor && <div className="janitor-email">Janitor · {u.today} today</div>}
                </div>
                <div className="janitor-actions">
                  {u.janitor && (
                    <button
                      className="code-copy-btn code-copy-btn--danger"
                      onClick={() => undoAll(u)}
                      disabled={busy !== null}
                      aria-label={`Revoke ${u.name} and undo all their changes`}
                      title="Revoke & undo all their changes"
                    >
                      <Undo2 size={14} />
                    </button>
                  )}
                  <button
                    className={`code-copy-btn user-janitor-toggle${u.janitor ? ' user-janitor-toggle--on' : ''}`}
                    onClick={() => toggle(u)}
                    disabled={busy !== null}
                    aria-pressed={u.janitor}
                    aria-label={u.janitor ? `Remove ${u.name} as janitor` : `Make ${u.name} a janitor`}
                    title={u.janitor ? 'Remove janitor' : 'Make janitor'}
                  >
                    <Brush size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.querySelector('.phone-frame') ?? document.body,
  );
}
